import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';

const galleryImages = [
  {
    id: 1,
    src: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=80',
    alt: 'Intérieur du café',
    category: 'ambiance'
  },
  {
    id: 2,
    src: 'https://images.unsplash.com/photo-1572442388796-11668a67e53d?w=800&q=80',
    alt: 'Cappuccino parfait',
    category: 'cafe'
  },
  {
    id: 3,
    src: 'https://images.unsplash.com/photo-1561522983-385a76fbb4cb?w=800&q=80',
    alt: 'Latte art',
    category: 'cafe'
  },
  {
    id: 4,
    src: 'https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=800&q=80',
    alt: 'Croissants frais',
    category: 'patisserie'
  },
  {
    id: 5,
    src: 'https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?w=800&q=80',
    alt: 'Espresso',
    category: 'cafe'
  },
  {
    id: 6,
    src: 'https://images.unsplash.com/photo-1671934944865-2a84561cdc1c?w=800&q=80',
    alt: 'Espace de travail',
    category: 'ambiance'
  },
  {
    id: 7,
    src: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=800&q=80',
    alt: 'Cookies maison',
    category: 'patisserie'
  },
  {
    id: 8,
    src: 'https://images.unsplash.com/photo-1518832168546-8e34741a9dfb?w=800&q=80',
    alt: 'Grains de café',
    category: 'cafe'
  },
  {
    id: 9,
    src: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=800&q=80',
    alt: 'Brioche dorée',
    category: 'patisserie'
  },
  {
    id: 10,
    src: 'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=800&q=80',
    alt: 'Double espresso',
    category: 'cafe'
  },
  {
    id: 11,
    src: 'https://images.unsplash.com/photo-1533134242443-d4fd215305ad?w=800&q=80',
    alt: 'Cheesecake',
    category: 'patisserie'
  },
  {
    id: 12,
    src: 'https://images.unsplash.com/photo-1542990253-0d0f5be5f0ed?w=800&q=80',
    alt: 'Chocolat chaud',
    category: 'cafe'
  }
];

const categories = [
  { id: 'all', name: 'Tout' },
  { id: 'ambiance', name: 'Ambiance' },
  { id: 'cafe', name: 'Café' },
  { id: 'patisserie', name: 'Pâtisseries' }
];

export default function GalleryPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = selectedCategory === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === selectedCategory);

  const pageTitle = "Galerie | Léger's Coffee Genève";
  const pageDescription =
    "Découvrez la galerie photo de Léger's Coffee à Genève : ambiance du coffee shop, cafés de spécialité, pâtisseries artisanales et moments gourmands.";
  const canonicalUrl = 'https://legerscoffee.fr/galerie';
  const ogImage = galleryImages[0]?.src || 'https://legerscoffee.fr/og-image.jpg';

  const structuredDataCollectionPage = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: "Galerie | Léger's Coffee",
    url: canonicalUrl,
    description: pageDescription,
    inLanguage: 'fr-CH'
  };

  const structuredDataImageGallery = {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: "Galerie photos Léger's Coffee",
    url: canonicalUrl,
    description: pageDescription,
    associatedMedia: galleryImages.map((image) => ({
      '@type': 'ImageObject',
      contentUrl: image.src,
      description: image.alt
    }))
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Léger's Coffee" />
        <meta property="og:locale" content="fr_CH" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify(structuredDataCollectionPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredDataImageGallery)}
        </script>
      </Helmet>

      <main data-testid="gallery-page" className="min-h-screen pt-20">
        {/* Hero */}
        <section className="bg-warm-secondary py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
                Notre Univers
              </span>
              <h1 className="font-serif text-4xl lg:text-5xl text-espresso font-semibold mb-6">
                Galerie
              </h1>
              <p className="text-soft-charcoal-secondary">
                Découvrez l'atmosphère chaleureuse de Léger's Coffee à travers notre galerie photos.
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="sticky top-20 z-40 bg-warm-alabaster border-b border-border-subtle py-4">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  data-testid={`filter-${cat.id}`}
                  className={`px-6 py-2 text-sm font-medium uppercase tracking-wider transition-all duration-300 ${
                    selectedCategory === cat.id
                      ? 'bg-espresso text-warm-alabaster'
                      : 'bg-transparent text-soft-charcoal hover:bg-warm-secondary'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-16 lg:py-24 bg-warm-alabaster">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredImages.map((image, index) => (
                <Dialog key={image.id}>
                  <DialogTrigger asChild>
                    <button
                      className="group relative aspect-square overflow-hidden bg-warm-secondary cursor-pointer opacity-0 animate-fade-in"
                      style={{ animationDelay: `${(index % 6) * 0.1}s` }}
                      data-testid={`gallery-image-${image.id}`}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/20 transition-colors duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-espresso/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <p className="text-warm-alabaster text-sm font-medium">{image.alt}</p>
                      </div>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-auto max-h-[80vh] object-contain"
                    />
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}